import type { ReportValueType } from '~/presentation/reports/common/type';
import type { IGetTextPrice } from '~/modules/shopify';

import {
  numberWithCommas,
  getTimeDurationLabel,
  getFormattedByInterval,
} from '~/modules/analytics';
import { EReportColumnKey, EReportDataType } from '~/presentation/reports/common/type';
import { NONE_VALUE } from '~/presentation/reports/viz/constants';
import { AnalyticInterval } from '~/modules/analytics/types';
import { formatReportTableDate } from '../helpers';
import { calcPercentageString } from '~/utils';

export const formatReportData = ({
  value,
  formatter,
  getTextPrice,
  name,
}: {
  value: ReportValueType;
  formatter?: string;
  getTextPrice: IGetTextPrice;
  name?: string;
}): ReportValueType => {
  const dataTypeIsObjectOrArray =
    value !== null && (typeof value === 'object' || Array.isArray(value));

  if (dataTypeIsObjectOrArray) return value;

  switch (formatter) {
    case EReportDataType.INTEGER: {
      return numberWithCommas((value ?? 0).toString());
    }

    case EReportDataType.CURRENCY: {
      return getTextPrice(value, false);
    }

    case EReportDataType.DATE: {
      return formatReportTableDate(value);
    }

    case EReportDataType.PERCENT: {
      if (typeof value !== 'number') return calcPercentageString(0, 1, 2) ?? '';
      return calcPercentageString(value / 100, 1, 2) ?? '';
    }

    case EReportDataType.DURATION: {
      return getTimeDurationLabel(Number(value));
    }

    case EReportDataType.STRING: {
      const fallbackValue = name === EReportColumnKey.CAMPAIGNS ? '' : NONE_VALUE;

      return value ?? fallbackValue;
    }

    case EReportDataType.DAY: {
      return getFormattedByInterval(value, AnalyticInterval.DAY, {
        isExpandDetail: true,
      });
    }

    case EReportDataType.HOUR: {
      return getFormattedByInterval(value, AnalyticInterval.HOUR, {
        isExpandDetail: true,
      });
    }

    case EReportDataType.MONTH: {
      return getFormattedByInterval(value, AnalyticInterval.MONTH, {
        isExpandDetail: true,
      });
    }

    case EReportDataType.YEAR: {
      return getFormattedByInterval(value, AnalyticInterval.YEAR);
    }

    case EReportDataType.WEEK: {
      return getFormattedByInterval(value, AnalyticInterval.WEEK, {
        isExpandDetail: true,
      });
    }

    case EReportDataType.QUARTER: {
      return getFormattedByInterval(value, AnalyticInterval.QUARTER, {
        isExpandDetail: true,
      });
    }

    case EReportDataType.OBJECT:
    case EReportDataType.ARRAY: {
      return value;
    }

    default:
      return `${value}`;
  }
};
